import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TagInput Component
 *
 * Reusable tag input for managing upload tags.
 * Supports both inline (card) and overlay (lightbox) variants.
 */
import { useState, useRef } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { cn } from '@/lib/utils';
export const TagInput = ({ tags, onAddTag, onRemoveTag, variant = 'inline', disabled = false, className, }) => {
    const [inputValue, setInputValue] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const inputRef = useRef(null);
    const handleKeyDown = async (e) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            e.stopPropagation();
            await handleAddTag();
        }
    };
    const handleAddTag = async () => {
        const tag = inputValue.trim();
        if (!tag || isAdding)
            return;
        // Check for duplicates
        if (tags.some((t) => t.tag === tag)) {
            setInputValue('');
            return;
        }
        setIsAdding(true);
        try {
            await onAddTag(tag);
            setInputValue('');
        }
        finally {
            setIsAdding(false);
        }
    };
    const handleRemoveTag = async (tag) => {
        await onRemoveTag(tag);
    };
    if (variant === 'overlay') {
        return (_jsxs("div", { className: cn('flex flex-wrap items-center justify-center gap-2', className), onClick: (e) => e.stopPropagation(), children: [tags.map((t) => (_jsxs("span", { className: "inline-flex items-center gap-1 bg-white/20 text-white text-xs px-2 py-1 rounded-full", children: [t.tag, _jsx("button", { type: "button", onClick: () => handleRemoveTag(t.tag), className: "hover:bg-white/20 rounded-full p-0.5 transition-colors", title: "Remove tag", disabled: disabled, children: _jsx(X, { className: "h-3 w-3" }) })] }, t.id))), _jsxs("div", { className: "inline-flex items-center gap-1 bg-white/10 rounded-full px-2 py-1", children: [_jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: (e) => setInputValue(e.target.value), onKeyDown: handleKeyDown, placeholder: "Add tag...", className: "bg-transparent text-white text-xs placeholder-white/50 outline-none w-20 min-w-0", disabled: disabled || isAdding }), _jsx("button", { type: "button", onClick: handleAddTag, disabled: disabled || isAdding || !inputValue.trim(), className: "hover:bg-white/20 rounded-full p-0.5 transition-colors disabled:opacity-50", title: "Add tag", children: isAdding ? (_jsx(Loader2, { className: "h-3 w-3 text-white animate-spin" })) : (_jsx(Plus, { className: "h-3 w-3 text-white" })) })] })] }));
    }
    // Inline variant (default)
    return (_jsxs("div", { className: cn('flex flex-wrap gap-1 p-1 border rounded bg-background min-h-[32px] items-center cursor-text', className), onClick: () => inputRef.current?.focus(), children: [tags.map((t, idx) => (_jsxs(Badge, { variant: "default", className: "text-xs gap-1 pr-1", children: [t.tag, _jsx("button", { type: "button", onClick: (e) => {
                            e.stopPropagation();
                            handleRemoveTag(t.tag);
                        }, className: "ml-1 hover:bg-primary-foreground/20 rounded-full p-0.5", disabled: disabled, children: _jsx(X, { className: "h-3 w-3" }) })] }, `${t.tag}-${idx}`))), _jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: (e) => setInputValue(e.target.value), onKeyDown: handleKeyDown, placeholder: tags.length === 0 ? 'Add tag...' : '', className: "flex-1 min-w-[60px] border-none outline-none text-xs bg-transparent", disabled: disabled || isAdding })] }));
};
export default TagInput;
